import {
  CheckCircle2,
  ChevronDown,
  Info,
  TriangleAlert,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

let overlayLockCount = 0;

function getFocusableElements(container: HTMLElement | null) {
  if (!container) return [];

  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getClientRects().length > 0,
  );
}

export function useOverlayDialog({
  lockScroll,
  onOpenChange,
  open,
}: {
  lockScroll: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (lockScroll) {
      overlayLockCount += 1;
      document.body.classList.add("ui-overlay-lock");
    }

    const animationFrame = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      const firstFocusable = getFocusableElements(dialog)[0];
      (firstFocusable ?? dialog)?.focus({ preventScroll: true });
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);

      if (lockScroll) {
        overlayLockCount = Math.max(0, overlayLockCount - 1);
        if (overlayLockCount === 0) {
          document.body.classList.remove("ui-overlay-lock");
        }
      }

      const previousFocus = previousFocusRef.current;
      if (previousFocus && document.contains(previousFocus)) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [lockScroll, open]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onOpenChange(false);
      return;
    }

    if (event.key !== "Tab") return;

    const dialog = dialogRef.current;
    const focusable = getFocusableElements(dialog);

    if (!dialog || focusable.length === 0) {
      event.preventDefault();
      dialog?.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && (activeElement === first || activeElement === dialog)) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return { dialogRef, handleKeyDown };
}

type ComponentSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "soft" | "ghost" | "danger" | "warm";
  size?: ComponentSize;
  fullWidth?: boolean;
  iconLeading?: ReactNode;
  iconTrailing?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      fullWidth = false,
      iconLeading,
      iconTrailing,
      size = "md",
      type = "button",
      variant = "primary",
      ...props
    },
    ref,
  ) => {
    const sizeClass = {
      sm: "ds-btn-sm",
      md: "ds-btn-md",
      lg: "ds-btn-lg",
    }[size];

    const variantClass = {
      primary: "ds-btn-blue",
      secondary: "ds-btn-secondary",
      soft: "ds-btn-outline",
      ghost: "ds-btn-ghost",
      danger: "ds-btn-danger",
      warm: "ds-btn-primary",
    }[variant];

    return (
      <button
        ref={ref}
        className={cx("ds-btn", sizeClass, variantClass, fullWidth && "ui-button-full", className)}
        type={type}
        {...props}
      >
        {iconLeading}
        {children}
        {iconTrailing}
      </button>
    );
  },
);

Button.displayName = "Button";

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
}

function Field({
  children,
  error,
  hint,
  htmlFor,
  label,
}: FieldProps & { children: ReactNode; htmlFor: string }) {
  return (
    <label className={cx("field", error && "field-invalid")} htmlFor={htmlFor}>
      {label ? (
        <span className="field-label-row">
          <span className="field-label">{label}</span>
        </span>
      ) : null}
      {children}
      {error ? (
        <span className="field-message field-message-error">{error}</span>
      ) : hint ? (
        <span className="field-hint">{hint}</span>
      ) : null}
    </label>
  );
}

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size">,
  FieldProps {
  iconLeading?: ReactNode;
  iconTrailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, hint, iconLeading, iconTrailing, id, label, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const hasAffix = iconLeading || iconTrailing;

    return (
      <Field error={error} hint={hint} htmlFor={inputId} label={label}>
        {hasAffix ? (
          <span className="ui-input-affix-wrap">
            {iconLeading ? (
              <span className="ui-input-leading-icon" aria-hidden>
                {iconLeading}
              </span>
            ) : null}
            <input
              ref={ref}
              className={cx(
                "ui-input",
                iconLeading ? "ui-input-with-leading-icon" : undefined,
                iconTrailing ? "ui-input-with-trailing-icon" : undefined,
                className,
              )}
            id={inputId}
            {...props}
            />
            {iconTrailing ? (
              <span className="ui-input-trailing-icon">{iconTrailing}</span>
            ) : null}
          </span>
        ) : (
          <input ref={ref} className={cx("ui-input", className)} id={inputId} {...props} />
        )}
      </Field>
    );
  },
);

Input.displayName = "Input";

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
  FieldProps { }

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, hint, id, label, ...props }, ref) => {
    const generatedId = useId();
    const textareaId = id ?? generatedId;

    return (
      <Field error={error} hint={hint} htmlFor={textareaId} label={label}>
        <textarea
          ref={ref}
          className={cx("ui-textarea", className)}
          id={textareaId}
          {...props}
        />
      </Field>
    );
  },
);

Textarea.displayName = "Textarea";

export interface SelectOption {
  label: string;
  value: string;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children">,
  FieldProps {
  options: SelectOption[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, error, hint, id, label, options, placeholder, ...props }, ref) => {
    const generatedId = useId();
    const selectId = id ?? generatedId;

    return (
      <Field error={error} hint={hint} htmlFor={selectId} label={label}>
        <select ref={ref} className={cx("ui-select", className)} id={selectId} {...props}>
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>
    );
  },
);

Select.displayName = "Select";

export function Checkbox({
  description,
  label,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  description?: string;
  label: string;
}) {
  return (
    <label className="ui-choice">
      <input type="checkbox" {...props} />
      <span>
        {label}
        {description ? <span className="ui-choice-description">{description}</span> : null}
      </span>
    </label>
  );
}

export function Toggle({
  checked,
  label,
  onCheckedChange,
}: {
  checked: boolean;
  label: string;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label className="ui-toggle">
      <input
        checked={checked}
        onChange={(event) => onCheckedChange(event.target.checked)}
        role="switch"
        type="checkbox"
      />
      <span className="ui-toggle-track" aria-hidden />
      <span>{label}</span>
    </label>
  );
}

export interface StepperStep {
  description?: string;
  label: string;
}

export function Stepper({
  currentStep,
  steps,
}: {
  currentStep: number;
  steps: StepperStep[];
}) {
  return (
    <ol className="ui-stepper" aria-label="Progress">
      {steps.map((step, index) => {
        const status =
          index < currentStep ? "complete" : index === currentStep ? "active" : "upcoming";

        return (
          <li className="ui-stepper-item" data-status={status} key={step.label}>
            <span className="ui-stepper-marker">
              {status === "complete" ? <CheckCircle2 aria-hidden size={15} /> : index + 1}
            </span>
            <span className="ui-stepper-copy">
              <span className="ui-stepper-label">{step.label}</span>
              {step.description ? (
                <span className="ui-stepper-description">{step.description}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function ChoiceCard({
  description,
  icon,
  meta,
  selected = false,
  title,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  description?: ReactNode;
  icon?: ReactNode;
  meta?: ReactNode;
  selected?: boolean;
  title: ReactNode;
}) {
  return (
    <button
      aria-pressed={selected}
      className={cx("ui-choice-card", selected && "ui-choice-card-selected")}
      type="button"
      {...props}
    >
      {icon ? <span className="ui-choice-card-icon">{icon}</span> : null}
      <span className="ui-choice-card-copy">
        <span className="ui-choice-card-title">{title}</span>
        {description ? <span className="ui-choice-card-description">{description}</span> : null}
      </span>
      {meta ? (
        <span className="ui-choice-card-aside">
          <span className="ui-choice-card-meta">{meta}</span>
        </span>
      ) : null}
    </button>
  );
}

export function Card({
  actions,
  children,
  className,
  description,
  footer,
  title,
  tone = "default",
}: {
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  description?: ReactNode;
  footer?: ReactNode;
  title?: ReactNode;
  tone?: "default" | "muted" | "accent";
}) {
  return (
    <section className={cx("ds-card ds-card-panel", "ui-card", tone !== "default" && `ui-card-${tone}`, className)}>
      {title || description || actions ? (
        <header className="ui-card-header">
          <div>
            {title ? <h2 className="ui-card-title">{title}</h2> : null}
            {description ? <p className="ui-card-description">{description}</p> : null}
          </div>
          {actions}
        </header>
      ) : null}
      {children ? <div className="ui-card-content">{children}</div> : null}
      {footer ? <footer className="ui-card-footer">{footer}</footer> : null}
    </section>
  );
}

export function Chip({
  children,
  size = "md",
  tone = "neutral",
}: {
  children: ReactNode;
  size?: "sm" | "md";
  tone?: "neutral" | "info" | "success" | "warning" | "danger";
}) {
  return <span className={cx("ui-chip", `ui-chip-${size}`, `ui-chip-${tone}`)}>{children}</span>;
}

export function Avatar({
  initials,
  name,
  size = "md",
  src,
}: {
  initials: string;
  name: string;
  size?: ComponentSize;
  src?: string;
}) {
  return (
    <span className={cx("ui-avatar", `ui-avatar-${size}`)} title={name}>
      {src ? <img alt="" src={src} /> : initials}
    </span>
  );
}

export function AvatarGroup({
  people,
  size = "md",
}: {
  people: Array<{ initials: string; name: string; src?: string }>;
  size?: ComponentSize;
}) {
  return (
    <div className="ui-avatar-group" aria-label={`${people.length} people`}>
      {people.map((person) => (
        <Avatar key={person.name} size={size} {...person} />
      ))}
    </div>
  );
}

export function Progress({
  label,
  value,
}: {
  label?: string;
  value: number;
}) {
  const bounded = Math.max(0, Math.min(100, value));

  return (
    <div className="ui-progress">
      {label ? (
        <div className="ui-progress-label-row">
          <span>{label}</span>
          <span>{bounded}%</span>
        </div>
      ) : null}
      <div className="ui-progress-track" role="progressbar" aria-valuemax={100} aria-valuemin={0} aria-valuenow={bounded}>
        <div className="ui-progress-bar" style={{ width: `${bounded}%` }} />
      </div>
    </div>
  );
}

export interface TabItem {
  badge?: ReactNode;
  content: ReactNode;
  label: string;
  value: string;
}

export function Tabs({
  defaultValue,
  items,
}: {
  defaultValue?: string;
  items: TabItem[];
}) {
  const [active, setActive] = useState(defaultValue ?? items[0]?.value);
  const activeItem = items.find((item) => item.value === active) ?? items[0];

  return (
    <div className="ui-tabs">
      <div className="ui-tab-list" role="tablist">
        {items.map((item) => (
          <button
            key={item.value}
            aria-selected={item.value === active}
            className="ui-tab"
            onClick={() => setActive(item.value)}
            role="tab"
            type="button"
          >
            {item.label}
            {item.badge ? <> {item.badge}</> : null}
          </button>
        ))}
      </div>
      <div role="tabpanel">{activeItem?.content}</div>
    </div>
  );
}

export interface TableColumn<T> {
  align?: "left" | "right";
  header: ReactNode;
  key: string;
  render: (row: T) => ReactNode;
  width?: string;
}

export function Table<T extends object>({
  columns,
  emptyState = "No rows yet.",
  getRowId,
  rows,
}: {
  columns: Array<TableColumn<T>>;
  emptyState?: ReactNode;
  getRowId: (row: T) => string;
  rows: T[];
}) {
  if (rows.length === 0) {
    return <div className="ui-empty">{emptyState}</div>;
  }

  return (
    <div className="ui-table-wrap">
      <table className="ui-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.align === "right" ? "ui-table-align-right" : undefined}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={getRowId(row)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={column.align === "right" ? "ui-table-align-right" : undefined}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const toastIcon: Record<ToastTone, LucideIcon> = {
  danger: XCircle,
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
};

type ToastTone = "info" | "success" | "warning" | "danger";

export function EmptyState({
  action,
  description,
  icon,
  title,
}: {
  action?: ReactNode;
  description: ReactNode;
  icon?: ReactNode;
  title: ReactNode;
}) {
  return (
    <div className="ui-empty-state">
      {icon ? <div className="ui-empty-state-icon">{icon}</div> : null}
      <h3 className="ui-empty-state-title">{title}</h3>
      <div className="ui-empty-state-description">{description}</div>
      {action ? <div className="ui-empty-state-action">{action}</div> : null}
    </div>
  );
}

export function Banner({
  action,
  description,
  title,
  tone = "info",
}: {
  action?: ReactNode;
  description?: ReactNode;
  title: ReactNode;
  tone?: ToastTone;
}) {
  const Icon = toastIcon[tone];

  return (
    <div className={cx("ui-banner", `ui-banner-${tone}`)} role="status">
      <Icon aria-hidden size={20} />
      <div className="ui-banner-copy">
        <div className="ui-banner-title">{title}</div>
        {description ? <div className="ui-banner-description">{description}</div> : null}
      </div>
      {action ? <div className="ui-banner-action">{action}</div> : null}
    </div>
  );
}

export function Toast({
  description,
  title,
  tone = "info",
}: {
  description?: ReactNode;
  title: ReactNode;
  tone?: ToastTone;
}) {
  const Icon = toastIcon[tone];

  return (
    <div className={cx("ui-toast", `ui-toast-${tone}`)} role="status">
      <Icon aria-hidden color="var(--color-blue-500)" size={20} />
      <div>
        <div className="ui-toast-title">{title}</div>
        {description ? <div className="ui-toast-description">{description}</div> : null}
      </div>
    </div>
  );
}

export function Tooltip({
  children,
  content,
}: {
  children: ReactNode;
  content: string;
}) {
  return (
    <span className="ui-tooltip" data-tooltip={content}>
      {children}
    </span>
  );
}

export function Modal({
  ariaLabel,
  bodyClassName,
  children,
  className,
  description,
  footer,
  header,
  onOpenChange,
  open,
  size = "md",
  title,
}: {
  ariaLabel?: string;
  bodyClassName?: string;
  children: ReactNode;
  className?: string;
  description?: ReactNode;
  footer?: ReactNode;
  header?: ReactNode;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  size?: "sm" | "md" | "lg";
  title?: ReactNode;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const { dialogRef, handleKeyDown } = useOverlayDialog({
    lockScroll: true,
    onOpenChange,
    open,
  });

  if (!open) {
    return null;
  }

  const labelledBy = title && !header ? titleId : undefined;

  return (
    <div
      className="ui-overlay"
      onMouseDown={(event) => {
        event.stopPropagation();
        onOpenChange(false);
      }}
      role="presentation"
    >
      <section
        aria-describedby={description && !header ? descriptionId : undefined}
        aria-label={!labelledBy ? ariaLabel : undefined}
        aria-labelledby={labelledBy}
        aria-modal="true"
        className={cx("ui-modal", size !== "md" && `ui-modal-${size}`, className)}
        onKeyDown={handleKeyDown}
        onMouseDown={(event) => event.stopPropagation()}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        {header ? (
          <header className="ui-modal-header ui-modal-header-custom">{header}</header>
        ) : (
          <header className="ui-modal-header">
            <div>
              <h2 className="ui-modal-title" id={titleId}>
                {title}
              </h2>
              {description ? (
                <p className="ui-modal-description" id={descriptionId}>
                  {description}
                </p>
              ) : null}
            </div>
            <button className="ui-close" onClick={() => onOpenChange(false)} type="button">
              <X aria-hidden size={18} />
              <span className="sr-only">Close</span>
            </button>
          </header>
        )}
        <div className={cx("ui-modal-body", bodyClassName)}>{children}</div>
        {footer ? <footer className="ui-modal-footer">{footer}</footer> : null}
      </section>
    </div>
  );
}

export function Drawer({
  children,
  className,
  description,
  footer,
  modal = true,
  onOpenChange,
  open,
  title,
}: {
  children: ReactNode;
  className?: string;
  description?: ReactNode;
  footer?: ReactNode;
  modal?: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  title: ReactNode;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const { dialogRef, handleKeyDown } = useOverlayDialog({
    lockScroll: modal,
    onOpenChange,
    open,
  });

  useEffect(() => {
    if (!open || modal) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const drawer = dialogRef.current;

      if (!drawer || !(event.target instanceof Node) || drawer.contains(event.target)) {
        return;
      }

      onOpenChange(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [dialogRef, modal, onOpenChange, open]);

  if (!open) {
    return null;
  }

  return (
    <>
      {modal ? (
        <div
          className="ui-drawer-overlay"
          onMouseDown={(event) => {
            event.stopPropagation();
            onOpenChange(false);
          }}
          role="presentation"
        />
      ) : null}
      <aside
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal={modal ? "true" : undefined}
        className={cx("ui-drawer", !modal && "ui-drawer-nonmodal", className)}
        onKeyDown={handleKeyDown}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <header className="ui-drawer-header">
          <div>
            <h2 className="ui-drawer-title" id={titleId}>
              {title}
            </h2>
            {description ? (
              <p className="ui-drawer-description" id={descriptionId}>
                {description}
              </p>
            ) : null}
          </div>
          <button className="ui-close" onClick={() => onOpenChange(false)} type="button">
            <X aria-hidden size={18} />
            <span className="sr-only">Close</span>
          </button>
        </header>
        <div className="ui-drawer-body">{children}</div>
        {footer ? <footer className="ui-drawer-footer">{footer}</footer> : null}
      </aside>
    </>
  );
}

export function PageHeader({
  actions,
  kicker,
  subtitle,
  title,
}: {
  actions?: ReactNode;
  kicker?: string;
  subtitle?: ReactNode;
  title: ReactNode;
}) {
  return (
    <header className="page-header-block">
      <div>
        {kicker ? <p className="page-header-kicker">{kicker}</p> : null}
        <h1 className="page-header-title ds-h1">{title}</h1>
        {subtitle ? <p className="page-header-subtitle ds-body">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-header-actions">{actions}</div> : null}
    </header>
  );
}

export { ChevronDown };
